/* ============================================================================
   DIAMOND EMPORIUM — DE·CIDE MARKETING EVENT TRACKING UTILITY
   File:      assets/de-tracking.js
   Owner:     INHOUSE OS / Robin — DE·CIDE marketing engine, Phase 1
   Built:     2026-08-19

   WHAT THIS FILE DOES
   - One single, reusable event-tracking utility (DE.track) that every other
     part of the marketing engine calls into. No brittle inline scripts
     scattered across template files.
   - UTM first-touch / last-touch attribution, persisted in first-party
     cookies (90-day first touch, 30-day last touch — never overwrites
     first touch once set).
   - DE·CIDE URL parameter support + a lightweight lead-state object,
     persisted in localStorage, that pre-fills and tracks the existing
     Diamond Shortlist form (sections/de-diamond-shortlist.liquid) — which
     IS Diamond Emporium's current DE·CIDE-equivalent lead intake flow.
   - WhatsApp click tracking on every wa.me / api.whatsapp.com link
     site-wide, with page/campaign context. Does NOT rewrite any existing
     pre-filled message text — only observes clicks.
   - Homepage/site-wide CTA hooks via a `data-de-event="event_name"`
     attribute convention, plus a small default map for the known nav
     links (ring-studio, diamonds, engagement-rings, diamond-guide,
     natural/lab-grown filters) so tracking works immediately with zero
     markup changes, and can be extended later just by adding attributes.

   WHAT THIS FILE DELIBERATELY DOES NOT DO
   - It never reads, wraps, or modifies anything inside a Nivoda block or
     any element/script whose id/class/src contains "nivoda". Nivoda is
     off-limits per project rules.
   - It never rewrites an existing WhatsApp href's pre-filled `text=`
     parameter. Those were hand-written on purpose (see de-homepage.liquid,
     de-diamond-guide.liquid, de-diamond-shortlist.liquid) and stay as-is.
   - It never sends anything to GA4/Meta/TikTok directly — those platforms
     are not connected yet. Every event lands in window.dataLayer (safe,
     inert array push) and as a `de:track` DOM CustomEvent, so wiring GA4 /
     Meta Pixel / TikTok Pixel later is a one-line listener, not a rewrite.

   HOW TO ADD A NEW TRACKED EVENT (no JS edits needed)
     <a href="/pages/ring-studio" data-de-event="start_de_cide" data-de-cta="hero_button">Design Your Ring</a>
   Any element with data-de-event fires that event on click, with every
   other data-de-* attribute folded into the event metadata automatically.

   DEBUGGING
     Add ?de_debug=1 to any URL, or run localStorage.setItem('de_debug','1')
     in the console, then watch the browser console for [DE] log lines.
   ============================================================================ */
(function () {
  'use strict';

  var COOKIE_DAYS_FIRST_TOUCH = 90;
  var COOKIE_DAYS_LAST_TOUCH  = 30;
  var DECIDE_STATE_KEY        = 'de_cide_state';

  var DEBUG = (function () {
    try {
      if (/[?&]de_debug=1\b/.test(window.location.search)) return true;
      return window.localStorage.getItem('de_debug') === '1';
    } catch (e) { return false; }
  })();

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[DE]');
    console.log.apply(console, args);
  }

  /* ---------------------------------------------------------------------
     Cookie helpers (first-party, SameSite=Lax, no third-party dependency)
  --------------------------------------------------------------------- */
  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ---------------------------------------------------------------------
     Sanitisation — every value that comes from a URL query string passes
     through this before it is stored or used anywhere. No HTML, no
     control characters, capped length. Prevents any injection via a
     crafted marketing link.
  --------------------------------------------------------------------- */
  function clean(val, maxLen) {
    if (val === null || val === undefined) return '';
    var s = String(val).slice(0, maxLen || 80);
    return s.replace(/[<>"'`]/g, '').trim();
  }

  /* ---------------------------------------------------------------------
     UTM + first-touch / last-touch attribution
  --------------------------------------------------------------------- */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function utmFromUrl() {
    var qs = new URLSearchParams(window.location.search);
    var out = {};
    UTM_KEYS.forEach(function (k) {
      if (qs.has(k)) out[k] = clean(qs.get(k), 80);
    });
    return out;
  }

  function persistAttribution() {
    var utm = utmFromUrl();
    var hasUtm = UTM_KEYS.some(function (k) { return utm[k]; });
    if (!hasUtm) return;

    var payload = {};
    UTM_KEYS.forEach(function (k) { payload[k] = utm[k] || ''; });
    payload.landing_page = window.location.pathname + window.location.search;

    if (!getCookie('de_first_touch')) {
      var first = Object.assign({ touched_at: new Date().toISOString() }, payload);
      setCookie('de_first_touch', JSON.stringify(first), COOKIE_DAYS_FIRST_TOUCH);
      log('first-touch attribution set', first);
    }

    var last = Object.assign({ touched_at: new Date().toISOString() }, payload);
    setCookie('de_last_touch', JSON.stringify(last), COOKIE_DAYS_LAST_TOUCH);
    log('last-touch attribution updated', last);
  }

  function getAttribution() {
    var out = {};
    try {
      var first = JSON.parse(getCookie('de_first_touch') || '{}');
      var last = JSON.parse(getCookie('de_last_touch') || '{}');
      Object.keys(first).forEach(function (k) { out['first_touch_' + k] = first[k]; });
      Object.keys(last).forEach(function (k) { out['last_touch_' + k] = last[k]; });
    } catch (e) { /* corrupt cookie, ignore */ }
    return out;
  }

  /* ---------------------------------------------------------------------
     DE·CIDE URL parameters + lead-state object
     Supported params: source, campaign, content, stage, product, ring,
     shape, origin, budget, keyword
     Persisted fields mirror the master spec's DE·CIDE state object.
  --------------------------------------------------------------------- */
  var DECIDE_PARAM_MAP = {
    shape:   'diamond_shape',
    origin:  'diamond_origin',
    budget:  'budget_band',
    product: 'preferred_product',
    ring:    'preferred_ring',
    keyword: 'inbound_keyword'
  };

  function readDecideState() {
    try { return JSON.parse(window.localStorage.getItem(DECIDE_STATE_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function writeDecideState(state) {
    try { window.localStorage.setItem(DECIDE_STATE_KEY, JSON.stringify(state)); }
    catch (e) { /* localStorage unavailable (private mode etc) — state just won't persist */ }
  }

  function mergeUrlIntoDecideState() {
    var qs = window.location.search ? new URLSearchParams(window.location.search) : null;
    if (!qs) return readDecideState();

    var state = readDecideState();
    state.completed_stages = state.completed_stages || [];

    Object.keys(DECIDE_PARAM_MAP).forEach(function (param) {
      if (qs.has(param)) state[DECIDE_PARAM_MAP[param]] = clean(qs.get(param), 60);
    });

    if (qs.has('stage')) {
      var stage = clean(qs.get('stage'), 30).toLowerCase();
      var validStages = ['discover', 'explain', 'compare', 'inspect', 'decide', 'ensure'];
      if (validStages.indexOf(stage) !== -1) {
        if (state.current_stage && state.current_stage !== stage &&
            state.completed_stages.indexOf(state.current_stage) === -1) {
          state.completed_stages.push(state.current_stage);
        }
        state.current_stage = stage;
      }
    }

    writeDecideState(state);
    return state;
  }

  function decideStateForTracking() {
    var s = readDecideState();
    return {
      decide_stage: s.current_stage || '',
      diamond_shape: s.diamond_shape || '',
      diamond_origin: s.diamond_origin || '',
      budget_band: s.budget_band || '',
      setting_style: s.setting_style || '',
      preferred_ring: s.preferred_ring || '',
      preferred_product: s.preferred_product || ''
    };
  }

  /* ---------------------------------------------------------------------
     DE.track — the single reusable event utility
     Every event automatically carries: page_url, referrer, UTM (if
     present in the current URL), first/last-touch attribution, and
     current DE·CIDE state. Callers only need to pass what's specific
     to that event.
  --------------------------------------------------------------------- */
  function track(eventName, metadata) {
    if (!eventName) return;
    metadata = metadata || {};

    var payload = Object.assign(
      {
        event: eventName,
        page_url: window.location.href,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString()
      },
      utmFromUrl(),
      getAttribution(),
      decideStateForTracking(),
      metadata
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    document.dispatchEvent(new CustomEvent('de:track', { detail: payload }));

    log('event:', eventName, payload);
  }

  /* ---------------------------------------------------------------------
     WhatsApp click tracking
     Fires on ANY click on a wa.me / api.whatsapp.com link, anywhere on
     the site (footer, homepage, policy pages, diamond guide, shortlist
     form CTA). Reads context from the nearest heading and from the
     existing pre-filled `text=` param — never modifies the href.
  --------------------------------------------------------------------- */
  function nearestHeadingText(el) {
    var section = el.closest('section, div[id], .de-dsl__form-section');
    if (!section) return '';
    var heading = section.querySelector('h1, h2, h3');
    return heading ? clean(heading.textContent, 80) : '';
  }

  function handleWhatsAppClick(link) {
    var href = link.getAttribute('href') || '';
    var numberMatch = href.match(/(?:wa\.me\/|phone=)(\d+)/);
    var textMatch = href.match(/[?&]text=([^&]*)/);

    track('whatsapp_clicked', {
      whatsapp_number: numberMatch ? numberMatch[1] : '',
      prefilled_message: textMatch ? decodeURIComponent(textMatch[1]).slice(0, 150) : '',
      section_heading: nearestHeadingText(link),
      link_text: clean(link.textContent, 60)
    });
  }

  /* ---------------------------------------------------------------------
     Default homepage / site-wide CTA hooks (section 18 of spec) —
     works with zero markup changes. Add data-de-event to override or
     extend beyond this list at any time.
  --------------------------------------------------------------------- */
  function defaultEventForLink(href) {
    if (!href) return null;
    if (/queryLabGrown=false|activeTab=Natural/i.test(href)) return 'natural_diamonds';
    if (/queryLabGrown=true|activeTab=LabGrown/i.test(href)) return 'lab_diamonds';
    if (/\/pages\/ring-studio/.test(href)) return 'start_de_cide';
    if (/\/pages\/diamonds(\?|$)/.test(href)) return 'shop_diamonds';
    if (/\/collections\/engagement-rings/.test(href)) return 'shop_rings';
    if (/\/pages\/diamond-guide/.test(href)) return 'diamond_guide';
    return null;
  }

  /* ---------------------------------------------------------------------
     Diamond Shortlist form (the site's existing DE·CIDE lead-intake flow)
     — sections/de-diamond-shortlist.liquid. Binds only if the form is
     present on the current page. Pre-fills selects from DE·CIDE URL
     params where the option text matches, tracks field-level DE·CIDE
     events, and tracks decide_started / decide_completed.
  --------------------------------------------------------------------- */
  var SHORTLIST_FIELD_EVENTS = {
    'dsl-budget':  'decide_budget_selected',
    'dsl-origin':  'decide_origin_selected',
    'dsl-shape':   'decide_shape_selected',
    'dsl-setting': 'decide_style_selected'
  };

  function selectOptionByFuzzyMatch(selectEl, value) {
    if (!value) return;
    var target = value.toLowerCase();
    var options = selectEl.querySelectorAll('option');
    for (var i = 0; i < options.length; i++) {
      var text = options[i].textContent.toLowerCase();
      if (text.indexOf(target) !== -1 || target.indexOf(text) !== -1) {
        selectEl.value = options[i].value || options[i].textContent;
        return;
      }
    }
  }

  function initShortlistForm(state) {
    var form = document.querySelector('#shortlist-form form, .de-dsl__form');
    if (!form) return;

    // Pre-fill from DE·CIDE state (URL params merged earlier in the page load)
    var shapeEl = document.getElementById('dsl-shape');
    var originEl = document.getElementById('dsl-origin');
    var budgetEl = document.getElementById('dsl-budget');
    if (shapeEl && state.diamond_shape) selectOptionByFuzzyMatch(shapeEl, state.diamond_shape);
    if (originEl && state.diamond_origin) selectOptionByFuzzyMatch(originEl, state.diamond_origin);
    if (budgetEl && state.budget_band) selectOptionByFuzzyMatch(budgetEl, state.budget_band);

    var startedFired = false;
    function fireStartedOnce() {
      if (startedFired) return;
      startedFired = true;
      track('decide_started', { entry_point: 'diamond_shortlist_form' });
    }

    Object.keys(SHORTLIST_FIELD_EVENTS).forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('focus', fireStartedOnce);
      el.addEventListener('change', function () {
        track(SHORTLIST_FIELD_EVENTS[id], { value: clean(el.value, 60) });
      });
    });

    form.addEventListener('submit', function () {
      // Fires before native navigation to /contact — does not block or
      // delay the actual form submission.
      var state = readDecideState();
      state.decide_completed = true;
      writeDecideState(state);
      track('decide_completed', { entry_point: 'diamond_shortlist_form' });
    });
  }

  /* ---------------------------------------------------------------------
     Global click delegation — one listener for the whole page, so it
     also catches links inside content injected after page load (as long
     as that content is NOT inside a Nivoda block, which this never
     touches or even queries).
  --------------------------------------------------------------------- */
  function initClickDelegation() {
    document.addEventListener('click', function (e) {
      var explicitEl = e.target.closest('[data-de-event]');
      if (explicitEl) {
        var meta = {};
        Array.prototype.forEach.call(explicitEl.attributes, function (attr) {
          if (attr.name.indexOf('data-de-') === 0 && attr.name !== 'data-de-event') {
            meta[attr.name.replace('data-de-', '').replace(/-/g, '_')] = clean(attr.value, 100);
          }
        });
        track(explicitEl.getAttribute('data-de-event'), meta);
        return; // explicit tag wins — don't also fire a default/whatsapp event for the same element
      }

      var waLink = e.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"]');
      if (waLink) {
        handleWhatsAppClick(waLink);
        return;
      }

      var navLink = e.target.closest('a[href]');
      if (navLink) {
        var evt = defaultEventForLink(navLink.getAttribute('href'));
        if (evt) track(evt, { link_text: clean(navLink.textContent, 60) });
      }
    }, true);
  }

  /* ---------------------------------------------------------------------
     Boot
  --------------------------------------------------------------------- */
  function init() {
    persistAttribution();
    var state = mergeUrlIntoDecideState();
    initClickDelegation();
    initShortlistForm(state);
    log('DE tracking ready', { debug: DEBUG, state: state });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose a small public API for future phases (metafield-driven product
  // events, email capture, etc.) to hook into without duplicating logic.
  window.DE = window.DE || {};
  window.DE.track = track;
  window.DE.getDecideState = readDecideState;
  window.DE.getAttribution = getAttribution;
})();
